import {
  singleItemWrapper,
  listItemWrapper,
  listWrapper,
  pagedListWrapper
} from '../../../api-client/wrappers'
import { createQuery } from '../../../api-client/wrappers/helpers'
import type { DeepPartial, Query } from '../../../api-client/wrappers/types'
import type { Starter } from '../../types/internal'
import type { ImageUrl } from '../../types/shared'
import type {
  SourceMember,
  Clan,
  Access,
  UpdateClanBody,
  CreateClanBody,
  UpdateStaffBody,
  UploadAvatarBody,
  Log,
  MemberRoleId,
  Member,
  Token,
  JoinClanByTokenBody,
  Invitation,
  Request,
  Settings,
  RatingPosition
} from './types'

export default {
  factory: ({ apiClient, version }) => {
    const {
      update: updateClan,
      create: createClan,
      remove: removeClan
    } = singleItemWrapper<Clan, UpdateClanBody, CreateClanBody>(apiClient, {
      path: `/api/v${version}/clans/manage`
    })

    const accesses = listWrapper<'id', Access>(apiClient, {
      path: `/api/v${version}/clans/manage/accesses`,
      idKey: 'id',
      requestOptions: {
        cache: {
          ttl: 600000 // 10m
        }
      }
    })

    const { update: updateStaff } = singleItemWrapper<Clan, UpdateStaffBody>(
      apiClient,
      {
        path: `/api/v${version}/clans/manage/staff`
      }
    )

    const { create: uploadAvatar, remove: removeAvatar } = singleItemWrapper<
      ImageUrl,
      any,
      UploadAvatarBody
    >(apiClient, {
      path: `/api/v${version}/clans/manage/avatar`
    })

    const log = pagedListWrapper<'id', Log>()(apiClient, {
      path: `/api/v${version}/clans/log`,
      idKey: 'id',
      paginationType: 'lastId'
    })

    const members = pagedListWrapper<
      'member_id',
      Member,
      { tag: Clan['tag'] }
    >()(apiClient, {
      path: (_query, { tag }) => `/api/v${version}/clans/${tag}/members`,
      idKey: 'member_id'
    })

    const membersSearch = pagedListWrapper<'id', SourceMember>()(apiClient, {
      path: `/api/v${version}/clans/members/search`,
      idKey: 'id'
    })

    const {
      get: getToken,
      create: createToken,
      remove: removeToken
    } = singleItemWrapper<Token>(apiClient, {
      path: `/api/v${version}/clans/manage/token`
    })

    const { get: getClanByToken } = singleItemWrapper<
      Clan,
      any,
      any,
      { token: Token['token'] }
    >(apiClient, {
      path: (_query, { token }) => `/api/v${version}/clans/token/${token}`
    })

    const { removeItem: removeInvitation } = listItemWrapper<Invitation>(
      apiClient,
      {
        path: `/api/v${version}/clans/manage/invites`
      }
    )

    const manageInvitations = pagedListWrapper<'id', Invitation>()(apiClient, {
      path: `/api/v${version}/clans/manage/invites`,
      idKey: 'id'
    })

    const memberInvitations = pagedListWrapper<'id', Invitation>()(apiClient, {
      path: `/api/v${version}/clans/invites`,
      idKey: 'id'
    })

    const manageRequests = pagedListWrapper<'id', Request>()(apiClient, {
      path: `/api/v${version}/clans/manage/requests`,
      idKey: 'id'
    })

    const { removeItem: removeRequest } = listItemWrapper<Request>(apiClient, {
      path: `/api/v${version}/clans/requests`
    })

    const memberRequests = pagedListWrapper<'id', Request>()(apiClient, {
      path: `/api/v${version}/clans/requests`,
      idKey: 'id'
    })

    const { get: getSettings } = singleItemWrapper<Settings>(apiClient, {
      path: `/api/v${version}/clans/settings`,
      requestOptions: {
        cache: {
          ttl: 600000 // 10m
        }
      }
    })

    const rating = pagedListWrapper<'rank', RatingPosition>()(apiClient, {
      path: `/api/v${version}/clans/rating`,
      idKey: 'rank'
    })

    const { get: getMyClan } = singleItemWrapper<Clan>(apiClient, {
      path: `/api/v${version}/clans/me`
    })

    const { getItem: getClanByTag } = listItemWrapper<Clan>(apiClient, {
      path: `/api/v${version}/clans`
    })

    return {
      updateClan,
      createClan,
      removeClan,
      accesses,
      updateStaff,
      uploadAvatar,
      removeAvatar,
      log,
      members,
      membersSearch,
      getToken,
      createToken,
      removeToken,
      getClanByToken,
      removeInvitation,
      manageInvitations,
      memberInvitations,
      manageRequests,
      removeRequest,
      memberRequests,
      getSettings,
      rating,
      getMyClan,
      getClanByTag,

      kickMember: (id: Member['member_id']) =>
        apiClient.post(`/api/v${version}/clans/members/${id}/kick`),

      changeMemberRole: (memberId: Member['member_id'], roleId: MemberRoleId) =>
        apiClient.post<Member>(
          `/api/v${version}/clans/members/${memberId}/role/${roleId}`
        ),

      joinClanByToken: (tag: Clan['tag'], body: JoinClanByTokenBody) =>
        apiClient.post(`/api/v${version}/clans/${tag}/join`, { body }),

      leaveClan: () => apiClient.post(`/api/v${version}/clans/leave`),

      inviteMember: <T extends DeepPartial<Invitation> = Invitation>(
        id: Member['member_id'],
        query: Query = {}
      ) =>
        apiClient.post<T>(`/api/v${version}/clans/members/${id}/invite`, {
          query: createQuery(query)
        }),

      acceptInvitation: <T extends DeepPartial<Invitation> = Invitation>(
        id: Invitation['id'],
        query: Query = {}
      ) =>
        apiClient.post<T>(`/api/v${version}/clans/invites/${id}/accept`, {
          query: createQuery(query)
        }),

      cancelInvitation: <T extends DeepPartial<Invitation> = Invitation>(
        id: Invitation['id'],
        query: Query = {}
      ) =>
        apiClient.post<T>(`/api/v${version}/clans/invites/${id}/cancel`, {
          query: createQuery(query)
        }),

      acceptRequest: <T extends DeepPartial<Request> = Request>(
        id: Request['id'],
        query: Query = {}
      ) =>
        apiClient.post<T>(
          `/api/v${version}/clans/manage/requests/${id}/accept`,
          { query: createQuery(query) }
        ),

      cancelRequest: <T extends DeepPartial<Request> = Request>(
        id: Request['id'],
        query: Query = {}
      ) =>
        apiClient.post<T>(
          `/api/v${version}/clans/manage/requests/${id}/cancel`,
          { query: createQuery(query) }
        ),

      createRequest: (tag: Clan['tag']) =>
        apiClient.post<{ request: Request }>(
          `/api/v${version}/clans/${tag}/requests`
        )
    }
  }
} satisfies Starter
